import { buildBookingRows, type BookingRow } from './booking';
import type { Invoice } from './types';
import type { IncomingInvoice } from './incoming/types';

/**
 * Serialisation of the Buchungsstapel. The accounting itself lives in booking.ts — this module
 * only turns rows into a file. Today: a reviewable semicolon CSV. The DATEV EXTF format for
 * direct import is the next step and will read from the same rows, so the two files can never
 * disagree about a booking.
 */

function amount(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

function csvField(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

const header = [
  'Belegdatum', 'Belegfeld 1', 'Umsatz', 'S/H', 'Konto', 'Gegenkonto',
  'BU-Schlüssel', 'Steuersatz', 'Buchungstext', 'KOST1', 'Richtung',
];

/** Renders booking rows as a semicolon-separated, German-number CSV. */
export function toDatevCsv(rows: readonly BookingRow[]): string {
  const lines = [header.join(';')];
  for (const row of rows) {
    lines.push([
      row.belegdatum,
      csvField(row.belegfeld1),
      amount(row.umsatz),
      row.sollHaben,
      row.konto,
      row.gegenkonto,
      row.buSchluessel,
      `${row.steuersatz}%`,
      csvField(row.buchungstext),
      csvField(row.kost1),
      row.richtung,
    ].join(';'));
  }
  return lines.join('\r\n');
}

/** Builds the CSV directly from both invoice sides. */
export function buildDatevCsv(invoices: readonly Invoice[], incoming: readonly IncomingInvoice[]): string {
  return toDatevCsv(buildBookingRows(invoices, incoming));
}

/** Triggers a browser download of the CSV (client-side, no server round-trip). */
export function downloadDatevCsv(csv: string, tenantShortName: string): void {
  // Leading BOM so Excel opens the file as UTF-8 and the umlauts survive.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `FORQ_Buchungsstapel_${tenantShortName}_DATEV.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
